import React, { Component } from "react";
import {
  Box,
  Card,
  Stack,
  ThemeProvider,
  Typography,
  createTheme,
} from "@mui/material";
import { createRoot } from "react-dom/client";

import LoginBanner from "./LoginBanner";
import MFALoginForm from "./MFALoginForm";

import { bizpost } from "../../ajax";
import scsmsLogo from "../../resources/scsms.png";
import { scsmsTokens as t } from "../../theme/brandTokens";

export type LoginStep = "CREDENTIALS" | "OTP_SELECT" | "OTP_VERIFY";
export type OtpChannel = "EMAIL" | "SMS";

const localTheme = createTheme({
  palette: {
    primary: {
      main: t.color.primary[700],
      dark: t.color.primary[800],
    },
    secondary: {
      main: t.color.secondary[700],
    },
    background: {
      default: t.color.neutral[50],
      paper: t.color.neutral[0],
    },
  },
  shape: {
    borderRadius: 16,
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
  },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: "0 20px 60px rgba(14, 30, 37, 0.10)",
        },
      },
    },
    MuiToggleButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
          fontWeight: 700,
          borderRadius: 10,
          paddingInline: 14,
        },
      },
    },
  },
});

interface Props {
  home: any;
  apptheme?: any;
  onMessage?: (type: string, title: string, message: string) => void;
  onForgotPassword?: () => void;
}

interface State {
  lang: "EN";
  step: LoginStep;
  email: string;
  password: string;
  otp: string;
  otpChannel: OtpChannel;
  loading: boolean;
  resendCountdown: number;
  passwordErrors: string[];
  showPassword: boolean;
}

export default class MFALoginMain extends Component<any, State> {
  private timer?: ReturnType<typeof setInterval>;

  state: State = {
    lang: "EN",
    step: "CREDENTIALS",
    email: "",
    password: "",
    otp: "",
    otpChannel: "EMAIL",
    loading: false,
    resendCountdown: 0,
    passwordErrors: [],
    showPassword: false,
  };

  componentWillUnmount() {
    if (this.timer) clearInterval(this.timer);
  }

  private notify(type: string, title: string, message: string) {
    if (this.props.onMessage) {
      this.props.onMessage(type, title, message);
      return;
    }
    window.alert(`${title}: ${message}`);
  }

  private validatePassword = (password: string): string[] => {
    const errors: string[] = [];
    if (password.length < 8) errors.push("At least 8 characters");
    if (!/[A-Z]/.test(password)) errors.push("At least one uppercase letter");
    if (!/[a-z]/.test(password)) errors.push("At least one lowercase letter");
    if (!/[0-9]/.test(password)) errors.push("At least one number");
    if (!/[!@#$%^&*(),.?":{}|<>_\-+=/\\[\];'`~]/.test(password)) {
      errors.push("At least one special character");
    }
    return errors;
  };

  private startResendTimer = () => {
    if (this.timer) clearInterval(this.timer);
    this.setState({ resendCountdown: 60 });

    this.timer = setInterval(() => {
      this.setState((prev) => {
        if (prev.resendCountdown <= 1) {
          if (this.timer) clearInterval(this.timer);
          return { resendCountdown: 0 };
        }
        return { resendCountdown: prev.resendCountdown - 1 };
      });
    }, 1000);
  };

  private handleCredentialSubmit = async () => {
    const { email, password } = this.state;
    this.setState({ loading: true });

    try {
      const response = await bizpost({
        s: "security",
        a: "logon",
        unm: email,
        eml: email,
        pwd: password,
      });

      if (!response?.success) {
        const err = response?.ed || {};
        this.notify(
          err.type || "warn",
          err.title || "Sign-in error",
          err.message || response?.em || "Sign-in failed."
        );
        return;
      }

      this.setState({ step: "OTP_SELECT" });
    } catch (error: any) {
      this.notify(
        error?.type || "error",
        error?.title || "Sign-in error",
        error?.message || "The sign-in request failed."
      );
    } finally {
      this.setState({ loading: false });
    }
  };

  private handleSendOtp = async () => {
    const { email, otpChannel } = this.state;
    this.setState({ loading: true });

    try {
      const response = await bizpost({
        s: "security",
        a: "otp",
        unm: email,
        eml: email,
        chn: otpChannel,
      });

      if (!response?.success) {
        const err = response?.ed || {};
        this.notify(
          err.type || "error",
          err.title || "OTP error",
          err.message || response?.em || "Failed to send the code."
        );
        return;
      }

      this.setState({ step: "OTP_VERIFY", otp: "" });
      this.startResendTimer();
    } catch (error: any) {
      this.notify(
        error?.type || "error",
        error?.title || "OTP error",
        error?.message || "Failed to send the OTP code."
      );
    } finally {
      this.setState({ loading: false });
    }
  };

  private handleVerifyOtp = async () => {
    const { home, apptheme } = this.props;
    const { lang, otp } = this.state;
    this.setState({ loading: true });

    try {
      const response = await bizpost({
        s: "security",
        a: "verify",
        otp,
        lang,
      });

      if (!response?.success) {
        const err = response?.ed || {};
        this.notify(
          err.type || "error",
          err.title || "Verification error",
          err.message || response?.em || "Invalid verification code."
        );
        return;
      }

      const container = document.getElementById("app");
      if (!container) throw new Error("App container not found");

      const root = createRoot(container);
      root.render(
        <ThemeProvider theme={apptheme || localTheme}>
          {React.createElement(home, { udata: response })}
        </ThemeProvider>
      );
    } catch (error: any) {
      this.notify(
        error?.type || "error",
        error?.title || "Verification error",
        error?.message || "OTP verification failed."
      );
    } finally {
      this.setState({ loading: false });
    }
  };

  render() {
    const {
      lang,
      step,
      email,
      password,
      otp,
      otpChannel,
      loading,
      resendCountdown,
      passwordErrors,
      showPassword,
    } = this.state;

    return (
      <ThemeProvider theme={localTheme}>
        <Box
          sx={{
            minHeight: "100dvh",
            boxSizing: "border-box",
            bgcolor: t.color.neutral[50],
            background:
              `linear-gradient(180deg, ${t.color.neutral[50]} 0%, ${t.color.primary[50]} 100%)`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            px: { xs: 2, md: 3 },
            py: { xs: 2, md: 2 },
            overflow: "auto",
          }}
        >
          <Card
            sx={{
              width: "100%",
              maxWidth: 1100,
              minHeight: {
                xs: "calc(100dvh - 32px)",
                lg: "max(640px, calc(100dvh - 32px))",
              },
              borderRadius: "12px",
              display: "flex",
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", lg: "0.95fr 1.05fr" },
                width: "100%",
                flex: 1,
              }}
            >
              <Box sx={{ display: { xs: "none", lg: "block" }, height: "100%" }}>
                <LoginBanner logoSrc={scsmsLogo} />
              </Box>

              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "flex-start",
                  px: { xs: 2.5, md: 3 },
                }}
              >
                <Box
                  sx={{
                    //px: { xs: 3, sm: 4, md: 5 },
                    //pt: { xs: 3, md: 4 },
                    //pb: { xs: 2, md: 2 },
                    px: { xs: 2.5, sm: 3, md: 3.5 },
                    pt: { xs: 2.5, md: 3 },
                    pb: { xs: 1.5, md: 1.5 },
                    borderBottom: "1px solid #EEF2F5",
                  }}
                >
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    spacing={2}
                  >
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <Box
                        component="img"
                        src={scsmsLogo}
                        alt="K-Frontiers"
                        sx={{
                          width: 44,
                          height: 44,
                          objectFit: "contain",
                        }}
                      />
                      <Stack spacing={0.25}>
                        <Typography
                          sx={{
                            fontSize: 12,
                            textTransform: "uppercase",
                            letterSpacing: 1.1,
                            color: t.color.neutral[500],
                            fontWeight: 800,
                          }}
                        >
                          Internal portal
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: 15,
                            color: t.color.neutral[800],
                            fontWeight: 700,
                          }}
                        >
                          SCSMS · K-FRONTIERS
                        </Typography>
                      </Stack>
                    </Stack>

                    <Typography sx={{ fontWeight: 800, color: t.color.primary[700] }}>
                      English
                    </Typography>
                  </Stack>
                </Box>

                <Box
                  sx={{
                    flex: 1,
                    display: "flex",
                    alignItems: "flex-start",
                    justifyContent: "flex-start",
                    px: { xs: 3, sm: 3, md: 4 },
                    py: { xs: 2, md: 3 },
                  }}
                >
                  <Box sx={{ width: "100%", maxWidth: 500 }}>
                    <Stack spacing={1.25} sx={{ mb: 3 }}>
                      <Typography
                        sx={{
                          fontSize: { xs: 26, md: 30 },
                          lineHeight: 1.2,
                          fontWeight: 800,
                          color: t.color.neutral[800],
                          letterSpacing: "-0.02em",
                        }}
                      >
                        Secure sign in
                      </Typography>

                      <Typography
                        sx={{
                          fontSize: 15,
                          color: t.color.neutral[500],
                          lineHeight: 1.7,
                        }}
                      >
                        Access the integrated sales, customer and stock management system.
                      </Typography>
                    </Stack>

                    <MFALoginForm
                      step={step}
                      email={email}
                      password={password}
                      otp={otp}
                      otpChannel={otpChannel}
                      loading={loading}
                      resendCountdown={resendCountdown}
                      passwordErrors={passwordErrors}
                      showPassword={showPassword}
                      onEmailChange={(value:any) => this.setState({ email: value })}
                      onPasswordChange={(value:any) =>
                        this.setState({
                          password: value,
                          passwordErrors: this.validatePassword(value),
                        })
                      }
                      onOtpChange={(value:any) => this.setState({ otp: value })}
                      onOtpChannelChange={(value:any) => this.setState({ otpChannel: value })}
                      onTogglePassword={() =>
                        this.setState((prev) => ({ showPassword: !prev.showPassword }))
                      }
                      onCredentialSubmit={this.handleCredentialSubmit}
                      onSendOtp={this.handleSendOtp}
                      onVerifyOtp={this.handleVerifyOtp}
                      onBackToCredentials={() =>
                        this.setState({ step: "CREDENTIALS", otp: "" })
                      }
                      onMessage={this.props.onMessage}
                      onForgotPassword={this.props.onForgotPassword}
                    />
                  </Box>
                </Box>
              </Box>
            </Box>
          </Card>
        </Box>
      </ThemeProvider>
    );
  }
}
